import * as React from 'react';
import './navigation-tabs.styles.scss';
import {Popover, PopoverProps, Tooltip, TooltipProps} from '../ant-design';


/**
 * NavigationTabs Optional Props
 *
 * @memberof NavigationTabs
 */
interface NavigationTabsOptionalProps {
    /**
     * Overrides the active tab
     */
    activeTabKey: string,
}

/**
 * NavigationTabs Props:
 * Has optional activeTabKey: string,
 *
 * @memberof NavigationTabs
 */
export interface NavigationTabsProps extends Required<NavigationTabsOptionalProps> {
    /**
     * Takes an array of Tabs. For typescript you can export Tab interface.
     * Tab:
     * {
     *  className: string,
     *  iconClassName?: string,
     *  icon?: Element | JSX.Element | HTMLElement,
     *  additionalTabStyle?: React.CSSProperties,
     *  additionalTabText?: string,
     *  additionalTabElements?: Element | JSX.Element | HTMLElement,
     *  displayName: string,
     *  key: string,
     * }
     */
    tabs: Tab[],

    /**
     * Passes clicked Tab via callback
     */
    onTabClick: (clickedTab: Tab) => void
};

/**
 * NavigationTabs State
 * @memberof NavigationTabs
 */
interface NavigationTabsState {
    /**
     * A State prop. ~~ Replace this with your state props
     */
    activeTabKey: string
};

/**
 * The NavigationTabs component.
 */
class NavigationTabs extends React.Component<NavigationTabsProps, NavigationTabsState> {

    /**
     * Default properties
     */
    static defaultProps: Required<NavigationTabsOptionalProps> = {
        activeTabKey: '',
    };

    constructor(props: NavigationTabsProps) {
        super(props);
        const activeTabKey = !!props.activeTabKey
        ? props.activeTabKey
        : props.tabs[0].key
        this.state = { activeTabKey: activeTabKey };
    }

    componentDidUpdate(prevProps:NavigationTabsProps) {
        /**
         * We want to allow passed in active tab to override state.
         * So if the prop active tab after update is different then previous one
         * and the current prop is not null/empty/undefined we will ensure that
         * the current state is also that value. Change the state to this value when
         * they differ.
         */
        const propsChanged = (prevProps.activeTabKey !== this.props.activeTabKey);
        const propAndStateMismatch = this.props.activeTabKey !== this.state.activeTabKey;
        if(this.props.activeTabKey && propsChanged && propAndStateMismatch){
            this.setState({activeTabKey: this.props.activeTabKey});
        }
    }

    changeTabs = (newTab:Tab) => {
        if (newTab.key !== this.state.activeTabKey){
            this.setState({activeTabKey: newTab.key});
        }
        this.props.onTabClick(newTab);
    }

    renderInnerNavigationTab = (tab: Tab, activeTabKey: string) => {
        const icon = (tab.iconClassName?.length! > 0) ? <><i className={'icon ' + tab.iconClassName}></i>&nbsp;</> : tab.icon;
        return <li
          key={`navigation-tabs-key-${tab.key}`}
          className={tab.className + ` nav-tab ${(tab.key === activeTabKey) ? 'active' : ''}`}
          onClick={() => this.changeTabs(tab)}
          style={tab.additionalTabStyle!}
        >
            {icon} {tab.displayName} {tab.additionalTabText!} {tab.additionalTabElements}
        </li>;
    }

    renderNavigationTab = (tab: Tab) => {
        const {activeTabKey} = this.state;
        const {popover: popoverProps, tooltip: tooltipProps} = tab;

        if (tooltipProps) {
            return <Tooltip key={`navigation-tabs-tooltip-key-${tab.key}`} {...tooltipProps}>
                {this.renderInnerNavigationTab(tab, activeTabKey)}
            </Tooltip>;
        }

        if (popoverProps) {
            return <Popover key={`navigation-tabs-popover-key-${tab.key}`} {...popoverProps}>
                {this.renderInnerNavigationTab(tab, activeTabKey)}
            </Popover>;
        }

        return this.renderInnerNavigationTab(tab, activeTabKey);
    }

    render() {
        const {tabs} = this.props;

        return (
            <div className='navigation-tabs'>
                <ul className='tab-list'>
                    {tabs.map((item) => this.renderNavigationTab(item))}
                </ul>
                {this.props.children}
            </div>
        );
    }
}

export interface Tab {
    className: string,
    iconClassName?: string,
    icon?: Element | JSX.Element | HTMLElement,
    additionalTabStyle?: React.CSSProperties,
    additionalTabText?: string,
    additionalTabElements?: Element | JSX.Element | HTMLElement,
    displayName: string,
    key: string,
    popover?: PopoverProps,
    tooltip?: TooltipProps,
}

export { NavigationTabs };
